import { normalizeArModelUrl } from "@/lib/utils/ar";

const CACHE_NAME = "smart-ar-models-v1";

/** @type {Map<string, string>} */
const memoryBlobUrls = new Map();

/**
 * @param {string} url
 * @returns {string}
 */
export function getArModelCacheKey(url) {
  return normalizeArModelUrl(url) || url;
}

/**
 * @param {string} cacheKey
 * @returns {Promise<string | null>}
 */
export async function getCachedModelSrc(cacheKey) {
  if (!cacheKey) return null;

  const memoryHit = memoryBlobUrls.get(cacheKey);
  if (memoryHit) return memoryHit;

  if (typeof caches === "undefined") return null;

  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(cacheKey);
    if (!cached) return null;

    const blob = await cached.blob();
    if (!blob.size) return null;

    const blobUrl = URL.createObjectURL(blob);
    memoryBlobUrls.set(cacheKey, blobUrl);
    return blobUrl;
  } catch {
    return null;
  }
}

/**
 * @param {string} cacheKey
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
async function storeModelBlob(cacheKey, blob) {
  const blobUrl = URL.createObjectURL(blob);
  memoryBlobUrls.set(cacheKey, blobUrl);

  if (typeof caches !== "undefined") {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(
        cacheKey,
        new Response(blob, {
          headers: {
            "Content-Type": blob.type || "model/gltf-binary",
          },
        })
      );
    } catch {
      // Cache API may be unavailable in private mode
    }
  }

  return blobUrl;
}

/**
 * Persist model in memory + Cache API (uses HTTP cache when model-viewer already fetched).
 * @param {string} cacheKey
 * @param {string[]} sources
 * @returns {Promise<string | null>}
 */
export async function warmArModelCache(cacheKey, sources) {
  if (!cacheKey || !sources?.length) return null;
  if (memoryBlobUrls.has(cacheKey)) return memoryBlobUrls.get(cacheKey);

  for (const source of sources) {
    try {
      const response = await fetch(source, {
        credentials: "same-origin",
        cache: "force-cache",
      });
      if (!response.ok) continue;

      const blob = await response.blob();
      if (!blob.size) continue;

      return storeModelBlob(cacheKey, blob);
    } catch {
      // try next source
    }
  }

  return null;
}
